import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  getNavigateToLoginOptions,
  setIntendedDestinationForLogin,
} from '../../utils/authSessionExpired';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useMediaQuery, Tooltip } from '@mui/material';
import {
  CustomAppBar,
  CustomToolbar,
  NavButtonsWrapper,
  NavBarSection,
  RightSection,
  IconGroup,
  MobileMenuButton,
  MobileDrawerContent,
  MobileDrawerLogo,
  MobileDrawerHeader,
  MobileDrawerList,
  NavLogoWrapper,
  NavLogo,
} from './RefinedNavBar.styled';
import { useAuth } from '../../context/AuthContext';
import { isLaunchMode } from '../../utils/launchMode';
import AuthModal from '../common/AuthModal/AuthModal';
import logo from '../../assets/images/nky_connect_logo_only.png';
import logoWithText from '../../assets/images/logoWithText.jpg';

const AI_SERVICE_SEARCH_PATH = '/ai-service-search';

const defaultNavPages = [
  { label: 'Home', path: '/' },
  { label: 'Find a Pro', path: '/services' },
  { label: 'Browse Pros', path: '/partners' },
  {label: 'Smart Search', path: AI_SERVICE_SEARCH_PATH, requiresAuth: true,},
  { label: 'About Us', path: '/about' },
];

function RefinedNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading: authLoading } = useAuth();
  const isLaunchGuest = isLaunchMode() && !user;
  const navPages =
    isLaunchGuest
      ? [{ label: 'Launch', path: '/launch' }]
      : defaultNavPages;
  const isMobile = useMediaQuery('(max-width:960px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const handleCloseAuthModal = () => setShowAuthDialog(false);

  const requireAuth = (action) => {
    if (authLoading) return;
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    action();
  };

  const handleNavClick = ({ path, requiresAuth: needsAuth }) => {
    const go = () => {
      navigate(path);
      setMobileOpen(false);
    };
    if (needsAuth) {
      requireAuth(go);
      return;
    }
    go();
  };

  const handleLoginLogout = () => {
    if (user) {
      logout();
    } else {
      navigate('/login', getNavigateToLoginOptions(location));
    }
    setMobileOpen(false);
  };

  const handleAccountClick = () => {
    setIntendedDestinationForLogin('/account');
    navigate('/account');
    setMobileOpen(false);
  };

  const handleDashboardClick = () => {
    setIntendedDestinationForLogin('/dashboard');
    navigate('/dashboard');
    setMobileOpen(false);
  };

  const handleAdminClick = () => {
    setIntendedDestinationForLogin('/admin');
    navigate('/admin');
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const mobileMenuContent = (
    <MobileDrawerContent>
      <MobileDrawerHeader>
        <Link
          to={isLaunchGuest ? '/launch' : '/'}
          onClick={handleDrawerToggle}
          style={{ display: 'flex', alignItems: 'center' }}
          aria-label="NKY Connect home"
        >
          <MobileDrawerLogo src={logoWithText} alt="NKY Connect" />
        </Link>
        <IconButton onClick={handleDrawerToggle} aria-label="Close menu" sx={{ color: '#1a1a2e' }}>
          <CloseIcon />
        </IconButton>
      </MobileDrawerHeader>
      <MobileDrawerList>
        {navPages.map((page) => (
          <ListItem key={page.label} disablePadding>
            <ListItemButton onClick={() => handleNavClick(page)}>
              <ListItemText
                primary={page.label}
                primaryTypographyProps={{
                  fontFamily: "'Exo 2', sans-serif",
                  fontWeight: 650,
                  fontSize: '18px',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        {user && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={handleAccountClick}>
                <ManageAccountsIcon sx={{ mr: 2, color: '#0d4efe' }} />
                <ListItemText
                  primary="Account"
                  primaryTypographyProps={{
                    fontFamily: "'Exo 2', sans-serif",
                    fontWeight: 650,
                    fontSize: '18px',
                  }}
                />
              </ListItemButton>
            </ListItem>
            {user.Role === 'admin_user' && (
              <ListItem disablePadding>
                <ListItemButton onClick={handleAdminClick}>
                  <AdminPanelSettingsIcon sx={{ mr: 2, color: '#0d4efe' }} />
                  <ListItemText
                    primary="Admin"
                    primaryTypographyProps={{
                      fontFamily: "'Exo 2', sans-serif",
                      fontWeight: 650,
                      fontSize: '18px',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
            {user.Role !== 'user' && (
              <ListItem disablePadding>
                <ListItemButton onClick={handleDashboardClick}>
                  <DashboardIcon sx={{ mr: 2, color: '#0d4efe' }} />
                  <ListItemText
                    primary="Dashboard"
                    primaryTypographyProps={{
                      fontFamily: "'Exo 2', sans-serif",
                      fontWeight: 650,
                      fontSize: '18px',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </>
        )}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLoginLogout}>
            <ListItemText
              primary={user ? 'Logout' : 'Login'}
              primaryTypographyProps={{
                fontFamily: "'Exo 2', sans-serif",
                fontWeight: 650,
                fontSize: '18px',
                color: '#0d4efe',
              }}
            />
          </ListItemButton>
        </ListItem>
      </MobileDrawerList>
    </MobileDrawerContent>
  );

  return (
    <CustomAppBar position="static">
      <Container maxWidth="xl">
        <CustomToolbar disableGutters>
          <NavBarSection>
            <Link to={isLaunchGuest ? '/launch' : '/'} aria-label="NKY Connect home">
              <NavLogoWrapper>
                <NavLogo src={logo} alt="NKY Connect" />
              </NavLogoWrapper>
            </Link>
            {isMobile && (
              <MobileMenuButton
                onClick={handleDrawerToggle}
                aria-label="open drawer"
              >
                <MenuIcon />
              </MobileMenuButton>
            )}
          </NavBarSection>

          {!isMobile && (
            <NavButtonsWrapper $compact={isLaunchGuest && navPages.length === 1}>
              {navPages.map((page) => (
                <Button
                  key={page.label}
                  onClick={() => handleNavClick(page)}
                  color="inherit"
                >
                  {page.label}
                </Button>
              ))}
            </NavButtonsWrapper>
          )}

          {!isMobile && (
            <RightSection>
              {user && (
                <IconGroup>
                  {user.Role !== 'user' && (
                    <Tooltip title="Business Dashboard">
                      <span style={{ display: 'inline-flex' }}>
                        <DashboardIcon onClick={handleDashboardClick} />
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="User Settings">
                    <span style={{ display: 'inline-flex' }}>
                      <ManageAccountsIcon onClick={handleAccountClick} />
                    </span>
                  </Tooltip>
                  {user.Role === 'admin_user' && (
                    <Tooltip title="Admin">
                      <span style={{ display: 'inline-flex' }}>
                        <AdminPanelSettingsIcon onClick={handleAdminClick} />
                      </span>
                    </Tooltip>
                  )}
                </IconGroup>
              )}
              <Button variant="contained" onClick={handleLoginLogout}>
                {user ? 'Logout' : 'Login'}
              </Button>
            </RightSection>
          )}

          {isMobile && (
            <RightSection>
              <Button variant="contained" onClick={handleLoginLogout}>
                {user ? 'Logout' : 'Login'}
              </Button>
            </RightSection>
          )}
        </CustomToolbar>
      </Container>
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {mobileMenuContent}
      </Drawer>

      <AuthModal
        open={showAuthDialog}
        onClose={handleCloseAuthModal}
        title="Account Required"
        message="To use Smart Search, please log in or create an account."
        postAuthRedirectPath={AI_SERVICE_SEARCH_PATH}
      />
    </CustomAppBar>
  );
}

export default RefinedNavBar;
